import { ref, computed } from "vue";
import Api from "@/model/api.js";
import Kodo from "@/model/kodo.js";
import { useGlobalStore } from "@/store/globalStore";
import Tracking from "@/model/tracking.js";

export const useEasyFile = (relativeUrl) => {
  const globalStore = useGlobalStore();
  const isWebToMpRedirectEnable = computed(
    () => globalStore.isWebToMpRedirectEnable
  );

  const supportImageContentType = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/webp",
  ];

  const supportVideoContentType = ["video/mp4"];

  const supportMarkdownContentType = [
    "text/markdown",
    "text/x-markdown",
    "application/x-markdown",
    "text/markdown; charset=utf-8",
    "text/x-markdown; charset=utf-8",
    "text/vnd.daringfireball.markdown",
    "text/plain",
    "text/plain; charset=utf-8",
  ];

  const fileUrl = ref(0);
  const easyFile = ref({});
  const isDownload = ref(false);
  const isMobile = ref(false);
  const isBanned = ref(false);

  const fileType = computed(() => {
    const contentType = easyFile.value.content_type;
    if (supportImageContentType.includes(contentType)) {
      return "image";
    }
    if (supportVideoContentType.includes(contentType)) {
      return "video";
    }
    if (supportMarkdownContentType.includes(contentType)) {
      return "markdown";
    }
    return "other";
  });

  // 获取文件信息
  const getEasyFile = async () => {
    try {
      const files = await Api.getEasyFile(relativeUrl);
      const file = files[0];
      easyFile.value = file;
      isBanned.value = file.is_banned == 1;

      // 如果文件在回收站中，直接返回 -1
      if (file.in_recycle_bin == 1) {
        fileUrl.value = -1;
        return;
      }

      if (file.is_transcoded == true) {
        fileUrl.value = await Kodo.getCdnUrl(file.transcoded_kodo_key);
      } else {
        if (file.kodo_key !== "" && file.kodo_key !== undefined) {
          fileUrl.value = await Kodo.getCdnUrl(file.kodo_key);
        } else if (file.oss_url !== "" && file.oss_url !== undefined) {
          fileUrl.value = file.oss_url;
        }
      }
    } catch (error) {
      fileUrl.value = -1;
    }
  };

  // 判断是否为微信环境
  const isInWeChat = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.match(/MicroMessenger/i) == "micromessenger";
  };

  // 处理文件下载
  const handleDownload = async () => {
    if (isInWeChat()) {
      ElMessage({
        message: "请在默认浏览器中打开以开始下载",
        type: "info",
        duration: 5000,
      });
    } else {
      try {
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = fileUrl.value;
        a.download = easyFile.value.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error(error);
      }
    }
  };

  // 判断是否为下载请求
  const isDownloadRequest = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("download");
  };

  // 处理打开小程序
  const handleOpenMiniProgram = async () => {
    const urlScheme = await Api.getUrlScheme("index");
    const cq = encodeURIComponent(`type=file&url=${relativeUrl}`);
    console.log("urlScheme", `${urlScheme}&cq=${cq}`);
    location.href = `${urlScheme}&cq=${cq}`;
  };

  // 处理举报功能
  const handleReport = async (ElMessageBox, ElMessage) => {
    try {
      await ElMessageBox.confirm("是否举报该文件？", "举报文件", {
        confirmButtonText: "举报",
        cancelButtonText: "取消",
        type: "warning",
      });

      await Api.createReport(easyFile.value.object_id);

      ElMessage({
        message: "已完成举报，我们会尽快审核",
        type: "success",
      });
    } catch (error) {
      // doing nothing
    }
  };

  // 初始化函数
  const initialize = async () => {
    await globalStore.fetchWebToMpRedirectEnable();
    isMobile.value = Tracking.platform() === "mobile-web";

    // 获取文件信息
    await getEasyFile();

    if (isBanned.value) {
      return;
    }

    if (isDownloadRequest()) {
      isDownload.value = true;
    } else if (isMobile.value && isWebToMpRedirectEnable.value) {
      handleOpenMiniProgram();
    }
  };

  return {
    state: {
      fileUrl,
      fileType,
      easyFile,
      isDownload,
      isMobile,
      isBanned,
      isWebToMpRedirectEnable,
    },
    action: {
      handleReport,
      handleOpenMiniProgram,
      handleDownload,
      initialize,
    },
  };
};
